/**
 * Kontakty API — typed wrappers around `/v1/contacts`.
 *
 * Mirrors the OpenAPI shapes (`ContactListResponseSchema`,
 * `ContactDetailResponseSchema`, brand + seller lookups) in
 * `api/src/schemas/contacts.ts`. Re-declared here so the module compiles when
 * the gitignored `frontend/src/types/api.ts` hasn't been refreshed.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { getAuthHeaders } from '@/lib/auth';

export interface Contact {
  id_kontaktu: number;
  jmeno_klienta: string | null;
  spolecnost: string;
  status: string | null;
  posledni_kontakt: string | null;
  typ_kontaktu: number;
  id_prodejce: number | null;
  podpis_objednavky: string | null;
  prodejce_jmeno: string;
  prodejce_barva: string | null;
  nazev_vozu: string | null;
  // Fields from findFiltered (the list endpoint)
  telefon?: string | null;
  email?: string | null;
  prvni_navsteva?: string | null;
  znacka_vozu?: number;
  automobil?: number;
  lms_id?: number;
  vysledek?: string | null;
  datum_ukonceni?: string | null;
  poznamka?: string | null;
  nazev_znacky?: string | null;
  nazev_modelu?: string | null;
  zastupce?: string | null;
  plan_dates?: string | null;
}

export type ContactType = 'rozjednane' | 'ukoncene' | 'vsechny';

const TYPE_PARAM: Record<ContactType, string> = {
  rozjednane: '1',
  ukoncene: '2',
  vsechny: '3',
};

export interface ContactListFilters {
  type: ContactType;
  name?: string;
  brand?: string;
  seller?: string;
  start?: string;
  end?: string;
  podpis_objednavky?: string;
  limit?: string;
  offset?: string;
}

interface ContactListResponse {
  contacts: Contact[];
  total: number;
}

function buildQuery(filters: ContactListFilters): string {
  const params = new URLSearchParams();
  params.set('type', TYPE_PARAM[filters.type]);
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'type') continue;
    if (value != null && value !== '') params.set(key, String(value));
  }
  return params.toString() ? `?${params.toString()}` : '';
}

export function useContactsQuery(
  filters: ContactListFilters,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['contacts', filters],
    queryFn: ({ signal }) =>
      api.get<ContactListResponse>(`/v1/contacts${buildQuery(filters)}`, { signal }),
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Paginated list query — feeds the `Načíst dalších N` button at the bottom of
 * the contacts table. Pass the same `filters` you'd give `useContactsQuery`,
 * minus `limit` / `offset` (the hook owns paging). Each page loads `pageSize`
 * rows; the next page's offset is the count we've already fetched. We stop
 * paging when `loaded >= total`.
 */
export function useContactsInfiniteQuery(
  filters: Omit<ContactListFilters, 'limit' | 'offset'>,
  options?: { enabled?: boolean; pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 300;
  return useInfiniteQuery({
    queryKey: ['contacts', 'infinite', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<ContactListResponse>(
        `/v1/contacts${buildQuery({ ...filters, limit: String(pageSize), offset: String(pageParam) })}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.contacts.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
}

export interface VehicleBrand {
  id: number;
  nazev: string;
}

interface VehicleBrandsResponse {
  brands: VehicleBrand[];
}

export function useContactBrandsQuery() {
  return useQuery({
    queryKey: ['contacts', 'vehicle-brands'],
    queryFn: ({ signal }) =>
      api.get<VehicleBrandsResponse>('/v1/contacts/vehicle-brands', { signal }),
    staleTime: 5 * 60 * 1000,
  });
}

export interface Seller {
  id: number;
  jmeno: string;
  prijmeni: string;
  barva: string | null;
}

interface SellersResponse {
  sellers: Seller[];
}

export function useContactSellersQuery() {
  return useQuery({
    queryKey: ['contacts', 'sellers'],
    queryFn: ({ signal }) =>
      api.get<SellersResponse>('/v1/contacts/sellers', { signal }),
    staleTime: 5 * 60 * 1000,
  });
}

export interface ContactMessage {
  id: number;
  datum_kontaktu: string;
  zprava: string;
  nabidnuto_bev: number;
  prodejce_jmeno: string;
  nazev_firmy: string | null;
}

export interface ContactPlan {
  id_planu: number;
  datum_planu: string;
  /** HH:MM, or null for a whole-day plan. */
  cas_planu: string | null;
  popis: string | null;
  stav: 'aktivni' | 'splneny' | 'zruseny';
  datum_vytvoreni: string | null;
  id_prodejce: number;
  prodejce_jmeno: string;
}

export interface ContactOsobniUdaje {
  ulice: string | null;
  cislo_popisne: string | null;
  psc: string | null;
  mesto: string | null;
  ico: string | null;
  dico: string | null;
  zastupce: string | null;
}

export interface ContactDetail {
  id_kontaktu: number;
  jmeno_klienta: string | null;
  spolecnost: string;
  telefon: string | null;
  email: string | null;
  prvni_navsteva: string | null;
  znacka_vozu: number;
  automobil: number;
  testovaci_jizda: string | null;
  posledni_kontakt: string | null;
  znalost_at: string | null;
  status: string | null;
  poznamka: string | null;
  id_prodejce: number | null;
  id_firmy: number | null;
  vysledek: string | null;
  podpis_objednavky: string | null;
  datum_ukonceni: string | null;
  datum_planovane: string | null;
  typ_kontaktu: number;
  lms_id: number;
  id_zakaznika: number;
  ulice: string | null;
  cislo_popisne: string | null;
  psc: string | null;
  mesto: string | null;
  ico: string | null;
  dico: string | null;
  zastupce: string | null;
  prodejce_jmeno: string;
  nazev_znacky: string | null;
  nazev_vozu: string | null;
  rok_uvedeni: number | null;
  nazev_modelu: string | null;
  osobni_udaje: ContactOsobniUdaje;
  zpravy: ContactMessage[];
  plany: ContactPlan[];
}

interface ContactDetailResponse {
  contact: ContactDetail;
}

export function useContactDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['contacts', id],
    enabled: id != null && Number.isFinite(id),
    // Backs the contact edit form — always refetch on mount so it opens against
    // fresh server state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<ContactDetailResponse>(`/v1/contacts/${id}`, { signal }),
  });
}

export interface VehicleModel {
  id: number;
  nazev: string;
  rok_uvedeni?: number;
}

interface VehicleModelsResponse {
  models: VehicleModel[];
}

export function useVehicleModelsQuery(brandId: number | null) {
  return useQuery({
    queryKey: ['contacts', 'vehicle-models', brandId],
    enabled: brandId != null && Number.isFinite(brandId) && brandId > 0,
    queryFn: ({ signal }) =>
      api.get<VehicleModelsResponse>(`/v1/contacts/vehicle-models/${brandId}`, {
        signal,
      }),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fields the create / update endpoints accept. The server schema is a
 * passthrough — these are the fields the kontakt form submits.
 */
export interface ContactBody {
  jmeno_klienta?: string;
  spolecnost?: string;
  ulice?: string;
  cislo_popisne?: string;
  psc?: string;
  mesto?: string;
  ico?: string;
  dico?: string;
  zastupce?: string;
  telefon?: string;
  email?: string;
  prvni_navsteva?: string;
  znacka_vozu?: number | string;
  automobil?: number | string;
  poznamka?: string;
  status?: string;
  testovaci_jizda?: string;
  posledni_kontakt?: string;
  znalost_at?: string;
  /**
   * Initial message recorded together with the new kontakt. The create
   * endpoint appends it as the first `kontakt_zpravy` row. Create-only — ignored on update.
   */
  zprava?: string;
  /**
   * Link the new kontakt to an existing customer record. Set by the
   * create flow when an unambiguous IČO match is detected against the
   * customers table — the lead lands pre-connected so downstream actions
   * (smlouva, faktura) inherit the customer record automatically.
   */
  id_zakaznika?: number;
}

interface ContactCreatedResponse {
  id: number;
}

export function useCreateContactMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContactBody) =>
      api.post<ContactCreatedResponse>('/v1/contacts', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useUpdateContactMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContactBody) =>
      api.put<{ status: 'ok' }>(`/v1/contacts/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts', id] });
    },
  });
}

interface ContactDuplicateResponse {
  id: number;
}

export function useDuplicateContactMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: number) =>
      api.post<ContactDuplicateResponse>(`/v1/contacts/${sourceId}/duplicate`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

interface ContactCreateCustomerResponse {
  status: 'ok';
  id_zakaznika: number;
}

/**
 * Create a brand-new customer from the contact's own data (company/name,
 * address, IČO, DIČ, zástupce, phone, email) and link them in one call — the
 * backend also sets `kontakt.id_zakaznika`. This is the "no existing match"
 * escape hatch from the Propojit modal; mirrors the legacy "Vytvořit zákazníka"
 * button. Invalidates both the contact (so the Crown shows the new link) and
 * the customer list.
 */
export function useCreateCustomerFromContactMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ContactCreateCustomerResponse>(`/v1/contacts/${id}/create-customer`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts', id] });
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

// ==================== LIFECYCLE ACTIONS (close · reopen · transfer) ====================

interface ContactFromCustomerResponse {
  id: number;
  id_zakaznika: number;
}

/**
 * Zákazník detail → "Vytvořit kontakt": `POST /v1/contacts/from-customer`
 * opens a lead pre-filled from the customer record (no message) and linked
 * back to it. Gated server-side by kontakty:write.
 */
export function useCreateContactFromCustomerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.post<ContactFromCustomerResponse>('/v1/contacts/from-customer', { id_zakaznika }),
    onSuccess: (_data, id_zakaznika) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['customers', id_zakaznika, 'summary'] });
    },
  });
}

export interface ContactCloseBody {
  vysledek?: string;
  podpis_objednavky?: 'Ano' | 'Ne';
  /** LMS rejection reason — only sent for LMS contacts closed without a signed order. */
  duvod_nezajmu?: number;
}

/** Close a contact (typ_kontaktu → 2). Backend syncs the closure to LMS when applicable. */
export function useCloseContactMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContactCloseBody) =>
      api.post<{ status: 'ok' }>(`/v1/contacts/${id}/close`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts', id] });
    },
  });
}

/** Reopen a finished contact (typ_kontaktu → 1). Backend re-opens the LMS lead when applicable. */
export function useReopenContactMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ status: 'ok' }>(`/v1/contacts/${id}/reopen`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts', id] });
    },
  });
}

/** Transfer a contact to another seller. */
export function useTransferContactMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (newSellerId: number) =>
      api.post<{ status: 'ok' }>(`/v1/contacts/${id}/transfer`, { id_prodejce: newSellerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts', id] });
    },
  });
}

export interface RejectionReason {
  id_duvodu: number;
  nazev_duvodu: string;
}

interface RejectionReasonsResponse {
  reasons: RejectionReason[];
}

/** LMS rejection reasons for the close-by-disinterest flow. Enabled only when needed. */
export function useRejectionReasonsQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['contacts', 'rejection-reasons'],
    queryFn: ({ signal }) =>
      api.get<RejectionReasonsResponse>('/v1/reference/rejection-reasons', { signal }),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

// ==================== MESSAGES (Phase 11.6.10) ====================

export interface ContactMessageBody {
  zprava: string;
  datum: string;
  status?: string;
  nabidnuto_bev?: 0 | 1;
}

export function useCreateContactMessageMutation(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContactMessageBody) =>
      api.post<{ status: 'ok' }>(`/v1/contacts/${contactId}/messages`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts', contactId] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

// ==================== PLANS ====================

export interface PlannedContact extends Contact {
  /** Latest plan date for this kontakt — surfaces on the Plánované list. */
  datum_planu?: string | null;
  /** HH:MM of that plan, null = whole day. */
  cas_planu?: string | null;
  /** Description of the latest plan. */
  plan_popis?: string | null;
  id_planu?: number | null;
}

interface PlannedContactsResponse {
  contacts: PlannedContact[];
  total: number;
}

export interface PlannedContactsFilters {
  name?: string;
  seller?: number | string;
}

export function usePlannedContactsQuery(
  filters: PlannedContactsFilters,
  options?: { enabled?: boolean },
) {
  const params = new URLSearchParams();
  if (filters.name) params.set('name', filters.name);
  if (filters.seller != null && String(filters.seller) !== '')
    params.set('seller', String(filters.seller));
  const qs = params.toString();
  return useQuery({
    queryKey: ['contacts', 'planned', filters],
    queryFn: ({ signal }) =>
      api.get<PlannedContactsResponse>(`/v1/contacts/planned${qs ? `?${qs}` : ''}`, {
        signal,
      }),
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
}

export interface PlanCreateBody {
  datum_planu: string;
  cas_planu?: string;
  id_prodejce?: number | string;
  popis?: string;
}

export interface PlanUpdateBody {
  datum_planu: string;
  cas_planu?: string;
  popis?: string;
}

function invalidatePlanCaches(qc: ReturnType<typeof useQueryClient>, contactId: number) {
  qc.invalidateQueries({ queryKey: ['contacts', contactId] });
  qc.invalidateQueries({ queryKey: ['contacts', 'planned'] });
  qc.invalidateQueries({ queryKey: ['contacts'] });
}

export function useCreatePlanMutation(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PlanCreateBody) =>
      api.post<{ status: 'ok' }>(`/v1/contacts/${contactId}/plans`, body),
    onSuccess: () => invalidatePlanCaches(qc, contactId),
  });
}

export function useUpdatePlanMutation(contactId: number, planId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PlanUpdateBody) =>
      api.put<{ status: 'ok' }>(`/v1/contacts/${contactId}/plans/${planId}`, body),
    onSuccess: () => invalidatePlanCaches(qc, contactId),
  });
}

export function useCompletePlanMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, planId }: { contactId: number; planId: number }) =>
      api.post<{ status: 'ok' }>(
        `/v1/contacts/${contactId}/plans/${planId}/complete`,
      ),
    onSuccess: (_data, { contactId }) => invalidatePlanCaches(qc, contactId),
  });
}

export function useCancelPlanMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, planId }: { contactId: number; planId: number }) =>
      api.post<{ status: 'ok' }>(
        `/v1/contacts/${contactId}/plans/${planId}/cancel`,
      ),
    onSuccess: (_data, { contactId }) => invalidatePlanCaches(qc, contactId),
  });
}

// ==================== STATISTICS ====================

/**
 * Aggregate breakdown returned by `/stats/year` and `/stats/total`.
 *
 * The endpoint returns `{}` when the aggregate row is missing, and MySQL
 * `SUM()` can serialize as a string or null (no matching rows) — so every
 * field is optional and loosely typed. Run values through `Number()` before
 * charting.
 */
export interface ContactStatsBreakdown {
  count?: number | string | null;
  rozjednane?: number | string | null;
  ukoncene_uspech?: number | string | null;
  ukoncene_neuspech?: number | string | null;
}

/**
 * Month-indexed counts from `/stats/monthly` + `/stats/messages-monthly` —
 * keys `'1'`..`'12'`, gap-filled to 0 server-side.
 */
export type ContactStatsMonths = Record<string, number>;

/** Row of `/stats/vehicles-monthly` — per-LMS-model counts within the year. */
export interface ContactVehicleStat {
  nazev_vozu: string | null;
  pocet_vozu: number;
}

function statsQs(params: { rok?: number; seller?: number | null }): string {
  const sp = new URLSearchParams();
  if (params.rok != null) sp.set('rok', String(params.rok));
  if (params.seller != null) sp.set('seller', String(params.seller));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function useContactStatsYearQuery(rok: number, seller: number | null) {
  return useQuery({
    queryKey: ['contacts', 'stats', 'year', rok, seller],
    queryFn: ({ signal }) =>
      api.get<ContactStatsBreakdown>(
        `/v1/contacts/stats/year${statsQs({ rok, seller })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export function useContactStatsTotalQuery(seller: number | null) {
  return useQuery({
    queryKey: ['contacts', 'stats', 'total', seller],
    queryFn: ({ signal }) =>
      api.get<ContactStatsBreakdown>(
        `/v1/contacts/stats/total${statsQs({ seller })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export function useContactStatsMonthlyQuery(rok: number, seller: number | null) {
  return useQuery({
    queryKey: ['contacts', 'stats', 'monthly', rok, seller],
    queryFn: ({ signal }) =>
      api.get<ContactStatsMonths>(
        `/v1/contacts/stats/monthly${statsQs({ rok, seller })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export function useContactStatsVehiclesMonthlyQuery(rok: number, seller: number | null) {
  return useQuery({
    queryKey: ['contacts', 'stats', 'vehicles-monthly', rok, seller],
    queryFn: ({ signal }) =>
      api.get<ContactVehicleStat[]>(
        `/v1/contacts/stats/vehicles-monthly${statsQs({ rok, seller })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

/** Per-seller monthly message counts — the endpoint requires `seller`. */
export function useContactStatsMessagesMonthlyQuery(rok: number, seller: number | null) {
  return useQuery({
    queryKey: ['contacts', 'stats', 'messages-monthly', rok, seller],
    enabled: seller != null,
    queryFn: ({ signal }) =>
      api.get<ContactStatsMonths>(
        `/v1/contacts/stats/messages-monthly${statsQs({ rok, seller })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

// ==================== ATTACHMENTS (Phase 4.6) ====================

export interface ContactAttachment {
  id_prilohy: number;
  nazev: string;
  poznamka: string;
  id_dokumentu: number;
  klic: string;
}

interface ContactAttachmentsResponse {
  attachments: ContactAttachment[];
}

function attachmentsBase(contactId: number) {
  return `/v1/contacts/${contactId}/attachments`;
}

export function useContactAttachmentsQuery(contactId: number | null) {
  return useQuery({
    queryKey: ['contacts', 'attachments', contactId],
    enabled: contactId != null && Number.isFinite(contactId),
    queryFn: ({ signal }) =>
      api.get<ContactAttachmentsResponse>(attachmentsBase(contactId as number), {
        signal,
      }),
  });
}

export function useUploadContactAttachmentMutation(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; nazev?: string; poznamka?: string }) => {
      const fd = new FormData();
      fd.append('file', input.file);
      if (input.nazev) fd.append('nazev', input.nazev);
      if (input.poznamka) fd.append('poznamka', input.poznamka);
      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...getAuthHeaders(),
      };
      const res = await fetch(`/api${attachmentsBase(contactId)}`, {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      return (await res.json()) as { id: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts', 'attachments', contactId] });
    },
  });
}

export function useUpdateContactAttachmentMutation(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { fileId: number; nazev: string; poznamka?: string }) =>
      api.put<{ status: 'ok' }>(
        `${attachmentsBase(contactId)}/${input.fileId}`,
        { nazev: input.nazev, poznamka: input.poznamka ?? '' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts', 'attachments', contactId] });
    },
  });
}

export function useDeleteContactAttachmentMutation(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: number) =>
      api.delete<{ status: 'ok' }>(`${attachmentsBase(contactId)}/${fileId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts', 'attachments', contactId] });
    },
  });
}
